import React from 'react';
import { SearchFieldContainer } from 'components/styled/SearchField/SearchField';
import { ButtonWrapper } from 'components/base/Dropdown/shared/styles';
import { MenuList } from 'components/base/Menu/Menu';
import Controls from 'components/base/TableControls/TableControls';
import styled from 'styled-components/macro';
import Layout from 'components/styled/Layout/Layout';
import modularScale from 'styles/helpers/modular-scale';
import { definitionValue } from 'styles/base-classes';

import {
  WhiteBlock,
  Toolbar,
} from 'components/utility/GenericComponent/GenericComponent';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';
import { Item } from 'components/base/Dropdown/shared/styles';
import { MenuRoot } from 'components/base/Menu/index';
import { Radio, Fieldset } from 'components/base/RadioGroup/RadioGroup';

import BaseTable from 'components/data-visualization/Table/Table';

import Row from 'components/data-visualization/Table/Row';
import BaseCell from 'components/data-visualization/Table/Cell';
import { ReactComponent as ToggleCaret } from 'icons/icon-toggle-caret.svg';
import {
  StyledTableHead,
  HeaderButton,
  ButtonContent,
} from 'components/data-visualization/Table/DropdownHead.style';
import { FlagImage } from 'components/base/CountryFlag';
import { Meta } from 'components/Metadata/Metadata.style';
import { StyledChangeArrow } from 'components/base/ChangeArrow/ChangeArrow';
import css from '@styled-system/css';

export const Table = styled(BaseTable)`
  thead th[data-hasdropdown='false'] div {
    justify-content: flex-start;
  }

  width: 100%;
`;

export const List = styled.ol`
  counter-reset: rankingCounter;
  display: flex;
  flex-flow: column nowrap;
  list-style: none;
  padding: 0;
  color: ${({ theme }): string => theme.DeepBlue};
  margin-top: 0;
`;

export const ContentWrapper = styled('section')`
  @media screen and (min-width: 40em) and (max-width: 799px) {
    padding: 0 1em;
  }

  ${WhiteBlock} {
    @media screen and (min-width: 40em) and (max-width: 799px) {
      margin: 0 0 1em;
      width: auto;
    }

    ${Layout.MaxWidth} {
      margin: 0 auto;
      width: 100%;
      max-width: 100%;
      padding: ${({ theme }): string => `0 ${theme.spacing.xsmall}`};

      @media screen and (min-width: 40em) {
        padding: 0;
      }

      ${AnimatedLoadingWrapper} {
        padding: 0 ${({ theme }): string => theme.spacing.small};
        @media screen and (min-width: 40em) {
          padding: 0 ${({ theme }): string => theme.spacing.xlarge};
        }
      }
    }

    ${Toolbar} {
      padding: ${({ theme }): string => theme.spacing.small};
      transform: none;
      @media screen and (min-width: 40em) {
        padding: ${({ theme }): string =>
          `${theme.spacing.xlarge} ${theme.spacing.xlarge} ${theme.spacing.small}`};
      }
    }
  }
`;

export const ShortFormVideoTableControls = styled(Controls)`
  display: block;
  padding: ${({ theme }): string => theme.spacing.small};
  @media screen and (min-width: 40em) {
    padding: 0 ${({ theme }): string => theme.spacing.xlarge};
  }
  ${MenuList} {
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
  }
  ${ButtonWrapper} {
    padding: ${({ theme }): string => `14px ${theme.spacing.medium}`};
    color: #373737;
  }
  ${Item} {
    padding: 2px ${({ theme }): string => theme.spacing.medium};
    font-size: 13px;
    font-weight: 600;
    line-height: 2.08;
    letter-spacing: 1.18px;
    color: ${({ theme }): string => theme.ShadowBlack};
  }
  ${MenuRoot} {
    margin-left: 0;
  }
`;

export const RadioGroupWrapper = styled('div')`
  ${Fieldset} {
    align-items: baseline;
  }
  ${Radio} + label {
    padding: 0.7rem 0;
    font-size: 16px;
    font-weight: 700;
    @media screen and (min-width: 50em) {
      padding: 0.58rem 1.4375rem;
    }
  }
`;

export const SearchFieldBlock = styled('div')<any>`
  margin: 0;

  ${SearchFieldContainer} {
    margin-bottom: ${({ alignCenteredVertically }): string =>
      alignCenteredVertically ? '0' : '3px'};
  }
`;

export const TableCell = styled(BaseCell)`
  padding: ${({ theme }): string => theme.spacing.small};
  display: table-cell;

  &:first-of-type {
    padding: ${({ theme }): string => theme.spacing.medium};
  }
`;

export const FirstTableCell = styled(TableCell)`
  font-weight: 600;
  text-align: left;

  &:first-of-type {
    padding-left: ${({ theme }): string => theme.spacing.xsmall};
  }
`;

export const TableTrendCell = styled(BaseCell)`
  padding: ${({ theme }): string => theme.spacing.xsmall};
  display: block !important;
  position: relative;
  top: ${({ theme }): string => theme.spacing.xsmall};
`;

export const CellContent = styled('div')<any>`
  ${definitionValue};
  font-weight: 400;
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  flex: 1 1 auto;
  color: ${({ changeColor }): string | false => changeColor && changeColor};

  @media screen and (min-width: 50em) {
    justify-content: flex-end;
    padding-right: ${({ theme, changeCell }): string =>
      changeCell ? theme.spacing.large : '3.1em'};
  }
`;

export const TableRow = styled(Row)<any>`
  display: flex;
  /* flex-flow: row wrap; */
  justify-content: space-between;
  width: 100%;
  border-bottom: 1px solid ${({ theme }): string => theme.LightGrey};
  background: ${({ isHighlighted, theme }): string | false | undefined =>
    isHighlighted && `${theme.Sand}`};

  td:not(:first-of-type) {
    font-size: ${modularScale(-1)};
  }

  td {
    &:not(:last-of-type) {
      display: flex;
      flex-basis: ${({ isExpandable }): string =>
        isExpandable ? '21.5%' : '25%'};
    }

    &:last-of-type {
      flex-basis: ${({ isExpandable }): string =>
        isExpandable ? '14%' : '25%'};
      justify-content: ${({ isExpandable }): string =>
        isExpandable ? 'flex-end' : 'center'};
      padding-right: ${({ isExpandable, theme }): string =>
        isExpandable && theme.spacing.xsmall};
    }

    display: flex;
    align-items: center;
  }
`;

export const ShortFormVideoTableWrapper = styled('div')<any>`
  ${StyledTableHead} {
    display: flex;
    width: 100%;
    justify-content: space-between;

    tr {
      display: flex;
      width: 100%;
      justify-content: space-between;

      th {
        &:not(:last-of-type) {
          display: flex;
          flex-basis: ${({ isExpandable }): string =>
            isExpandable ? '21.5%' : '25%'};
        }

        &:nth-child(4) {
          justify-content: flex-end;
        }

        &:nth-child(3) {
          justify-content: flex-end;
          text-align: center;
        }

        &:nth-child(2) {
          justify-content: center;
        }

        &:last-of-type {
          ${({ isExpandable }): any =>
            isExpandable &&
            css`
              display: flex;
              flex-basis: 14%;
            `};
        }
      }

      ${HeaderButton} {
        text-align: center;

        span {
          color: ${({ theme }): string => theme.CasperGrey};
          font-weight: normal;
        }
      }
      ${ButtonContent} {
        .chevron {
          color: ${({ theme }): string => theme.CasperGrey};
        }
      }
    }
  }
`;

export const ValueWrapper = styled.div`
  margin-right: 0.5em;
  text-align: right;
  text-transform: lowercase;
  font-size: 16px;
  min-width: 5.8em;
`;

export const CountryNameWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`;

export const ExpandButton = styled.button`
  padding: 0;
  border-radius: 50%;
  width: 2em;
  height: 2em;

  &:hover,
  &:focus,
  &:active {
    background: ${({ theme }): string => theme.LightGrey};
  }
`;

export const EntityCell = styled('td')`
  display: block;

  &:first-of-type {
    padding: 0;

    ${CellContent} {
      justify-content: flex-start;
    }
  }

  &:last-of-type {
    margin-right: 0;
  }

  &:first-child:last-child {
    padding-bottom: ${({ theme }): string => theme.spacing.small};
  }

  @media screen and (min-width: 50em) {
    display: table-cell;

    &,
    &:first-of-type {
      padding-top: 0;
      padding-bottom: 0;
    }

    &:first-child:last-child {
      padding-bottom: 0;
    }
  }
`;

const ToggleCaretWrapper = (props: any): JSX.Element => {
  const { children, ...rest } = props;
  delete rest.isPointingUp;

  return <ToggleCaret {...rest}>{children}</ToggleCaret>;
};

export const CaretIcon = styled(ToggleCaretWrapper)`
  transform: ${({ isPointingUp }): string =>
    isPointingUp ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 200ms linear;

  &:active {
    transform: scale(0.95);
  }
`;

export const ListItem = styled.li<any>`
  padding: ${({ theme }): string =>
    `${theme.spacing.medium} ${theme.spacing.xsmall}`};
  border-bottom: 1px solid ${({ theme }): string => theme.Grey};
  background: ${({ isHighlighted, theme }): string =>
    isHighlighted && theme.LightGrey};
  position: relative;
`;

export const TerritoryDataListItem = styled.div`
  display: flex;
  flex-direction: row;

  ${FlagImage} {
    height: 29px;
    width: 40px;

    margin: ${({ theme }): string =>
      `${theme.spacing.medium} ${theme.spacing.xsmall}`};
  }
`;

export const CountryData = styled.div`
  display: flex;
  flex-direction: column;

  margin: ${({ theme }): string => `0 ${theme.spacing.xsmall}`};
`;

export const CountryName = styled('p')`
  font-size: 1em;
  padding: 0;
  margin: 0;
  font-weight: 600;
  color: #00082e;
`;

export const Metric = styled('p')<any>`
  margin: 0;
  font-size: ${modularScale(-2)};
  text-transform: ${({ isUpperCase }): string =>
    isUpperCase ? 'uppercase' : 'initial'};
  font-weight: 600;
  color: ${({ theme, hasColor }): string =>
    hasColor ? theme[hasColor] : theme.ShadowBlack};
  @media screen and (min-width: 50em) {
    font-size: ${modularScale(-1)};
  }

  ${StyledChangeArrow} {
    span {
      width: 12px;
      height: 7px;
    }
  }
`;

export const MetaDetails = styled(Meta)<any>`
  margin: 0 ${({ theme }): string => theme.spacing.xsmall} 0 0;
  font-size: ${modularScale(-2)};
  font-weight: 600;
  text-transform: ${({ isUpperCase }): string =>
    isUpperCase ? 'uppercase' : 'initial'};
  line-height: 1.6;
  @media screen and (min-width: 50em) {
    font-size: ${modularScale(-1)};
  }
`;

export const MetricsList = styled('ul')`
  padding: 0;
`;

export const TrendChartWrapper = styled('div')`
  padding: ${({ theme }): string =>
    `${theme.spacing.medium} ${theme.spacing.medium} 0`};
`;

export const TrendLabel = styled(Meta)`
  font-size: ${modularScale(-2)};
  text-transform: uppercase;
  text-align: center;
  font-weight: 600;
`;

export const ExpandableRow = styled.tr`
  background: ${({ theme }): string => theme.Sand};
  border: 0;
  border-collapse: collapse;
  padding-left: 3.5rem;
`;

export const ExpandableCell = styled.td`
  padding: 0;
`;

export const ExpandableListItem = styled(ListItem)<{ isExpanded: boolean }>`
  display: ${({ isExpanded }): string => (isExpanded ? 'block' : 'none')};
  padding: 0;
`;

export const TopRightCorner = styled.div`
  position: absolute;
  top: 30px;
  right: 5px;
`;
